import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import SidebarToggle from '@/components/SidebarToggle'
import { BookIcon, GlobeIcon } from '@/components/Icons'
import { useBacklogConnection } from '@/hooks/useBacklogConnection'
import { useBacklogConnectNudge } from '@/hooks/useBacklogConnectNudge'
import { useUiStrings } from '@/hooks/useUiStrings'
import { takeReturnPath } from '@/utils/backlogReturnPath'
import { SETTINGS_TAB_PARAM } from '@/constants/settings-tabs'
import { isBacklogApprovalWindow, notifyBacklogOpener } from '@/utils/backlogConnectPopup'
import AppearanceSettings from './AppearanceSettings'
import BacklogSettings from './BacklogSettings'

/**
 * Which panel is on screen, carried in the URL.
 *
 * `general` is the default: it is the one every reader has a reason to open,
 * and an integration they may never use should not be what the screen opens
 * on.
 *
 * The Backlog approval is the exception, and it is handled where the callback
 * is read rather than here. The application registers one redirect URI, so the
 * browser comes back to bare `/settings` with no way to say which tab the trip
 * was about — the redeem below puts `?tab=backlog` on instead of clearing the
 * query outright, so the reader lands on the card they left from.
 */
type SettingsTab = 'general' | 'backlog'


function readTab(value: string | null): SettingsTab {
  return value === 'backlog' ? 'backlog' : 'general'
}

/**
 * Everything that belongs to the reader rather than to one meeting.
 *
 * A page rather than a dialog because it has to be reachable before any meeting exists: the same
 * panel sits beside the Sync button on a meeting's own screen, and that screen cannot be reached
 * until there is a meeting to attach the grant's projects to.
 *
 * Two groups, because they answer different questions: how this browser should
 * look and read, and which outside services this account has approved. Language
 * and theme moved here out of the account menu — they were there because there
 * was nowhere else, and nobody looking for the language setting opens a menu
 * under their own name.
 */
export default function SettingsPage() {
  const { t } = useUiStrings()
  const backlog = useBacklogConnection()
  // The same answer the sidebar dot reads, so the two cannot disagree: a reader
  // who came here because of that dot finds the same mark on the tab it was
  // pointing at. It also keeps the mark off a space that does not use Backlog
  // at all, where a dot would be an invitation to do nothing.
  const needsBacklogConnection = useBacklogConnectNudge()

  // Backlog sends the browser back here, because this page is where the connection lives and a
  // callback route of its own would exist only to redirect to it.
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const code = searchParams.get('code')
  const stateParam = searchParams.get('state')
  const { completeSso } = backlog

  const tab = readTab(searchParams.get(SETTINGS_TAB_PARAM))

  /** Replaces rather than pushes: a tab is where you are, not somewhere you went. */
  function selectTab(next: SettingsTab) {
    setSearchParams(next === 'general' ? {} : { [SETTINGS_TAB_PARAM]: next }, { replace: true })
  }

  /**
   * True from the very first paint of a callback URL.
   *
   * Seeded from the URL rather than set in the effect, because the effect runs
   * after that first paint: the settings screen would be on screen before
   * anything could decide it should not be. It is the reason the reader used
   * to see this page blink past on the way back to their minutes.
   *
   * Not derived from `code` either — the code is stripped out of the URL
   * immediately, long before the exchange it belongs to has finished.
   */
  const [redeeming, setRedeeming] = useState(() => Boolean(code && stateParam))

  const redeemedCode = useRef<string | null>(null)

  useEffect(() => {
    if (!code || !stateParam) return

    // Once per code, and a ref is what it takes. StrictMode mounts this twice in development with
    // the same captured params, and stripping the URL cannot prevent the second run — both happen
    // before the re-render lands. The second redemption spends a code Backlog has already retired,
    // which comes back 400 and reports a failure for something that worked.
    if (redeemedCode.current === code) return
    redeemedCode.current = code

    // Stripped as well as guarded: the ref dies with the component, so a refresh or a pasted link
    // would otherwise arrive with the spent code still in the address bar.
    // `tab=backlog`, not an empty query: the reader left from the Backlog card
    // and the callback has no way to say so, and dropping them on the language
    // panel after approving a tracker reads as having been sent somewhere else.
    setSearchParams({ [SETTINGS_TAB_PARAM]: 'backlog' }, { replace: true })
    void completeSso(code, stateParam).then((connected) => {
      // Only on success, and only back to a screen this app served. A refused
      // grant stays here, where the error and the button to try again are.
      //
      // `replace`, so Back does not walk the reader into a settings page
      // holding a spent code.
      //
      // A trip that began here now needs the special case the comment that
      // used to sit here said it did not. That was true while the redeem
      // cleared the query: navigating to a stored `/settings` from `/settings`
      // changed nothing. It writes `?tab=backlog` instead now, and navigating
      // to the bare stored path drops it again — landing the reader on the
      // language panel having just approved a tracker.
      const returnTo = takeReturnPath()
      // Opened as the approval window from a meeting (BRAI-689). Reporting
      // back and getting out of the way is the whole job here: the screen that
      // opened this one is still recording, and it does not want a settings
      // page it never asked for — still less this window navigating itself to
      // the meeting, which the inherited `returnTo` would otherwise do.
      if (connected && isBacklogApprovalWindow()) {
        notifyBacklogOpener()
        window.close()
      } else if (connected && returnTo && returnTo !== pathname) {
        navigate(returnTo, { replace: true })
      }
      // Cleared unconditionally, including on the way out. Skipping it to
      // avoid a frame of settings before the unmount cost more than it saved:
      // the two updates batch, so that frame never rendered anyway — and when
      // the trip began on this very screen there is no unmount to rely on, so
      // the waypoint span for ever.
      setRedeeming(false)
    })
  }, [code, stateParam, completeSso, setSearchParams, navigate, pathname])

  if (redeeming) {
    // Deliberately not the settings screen: for a reader on their way back to
    // a meeting, this page is a waypoint they never asked for, and showing it
    // reads as having been dropped somewhere else.
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4">
        <span
          aria-hidden
          className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--border-color)] border-t-[var(--accent)] motion-reduce:animate-none"
        />
        <p role="status" className="text-sm text-[var(--text-secondary)]">
          {t.backlogFinishingConnection}
        </p>
      </div>
    )
  }

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 sm:px-6">
        <SidebarToggle />
        <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-[var(--text-primary)]">
          {t.settings}
        </h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
        {/* Stacks below `sm`: the nav is six words, and a 200px rail beside a
            card on a phone leaves neither enough room. */}
        {/* Left-aligned and full width, not a centred column. `mx-auto max-w-4xl`
            put a gutter between the app's own sidebar and this screen's rail,
            which read as the page having shrunk away from the navigation it
            belongs to. */}
        <div className="flex flex-col gap-5 sm:flex-row sm:gap-6">
          {/* `w-56`, not `w-52`: "Language & appearance" is 163px and the narrower
              rail gave it 156, so it arrived ellipsised. `truncate` stays as the
              fallback for a translation longer than either. */}
          <nav aria-label={t.settings} className="sm:w-56 sm:shrink-0">
            <ul className="space-y-1">
              <li>
                <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {t.settingsGroupGeneral}
                </p>
              </li>
              <li>
                <TabButton
                  active={tab === 'general'}
                  icon={<GlobeIcon className="h-4 w-4 shrink-0" />}
                  label={t.settingsAppearanceNav}
                  onSelect={() => selectTab('general')}
                />
              </li>
              <li>
                <p className="px-3 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {t.settingsGroupIntegrations}
                </p>
              </li>
              <li>
                <TabButton
                  active={tab === 'backlog'}
                  icon={<BookIcon className="h-4 w-4 shrink-0" />}
                  label={t.settingsBacklogCardTitle}
                  needsAttention={needsBacklogConnection ? t.backlogNudgeSidebar : undefined}
                  onSelect={() => selectTab('backlog')}
                />
              </li>
            </ul>
          </nav>

          <div className="min-w-0 flex-1">
            {tab === 'general' ? (
              <AppearanceSettings t={t} />
            ) : (
              <BacklogSettings connection={backlog} t={t} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * One row of the settings nav.
 *
 * A button rather than a link: the tab lives in a query parameter on the page
 * the reader is already on, so there is no document to navigate to and an
 * anchor would claim otherwise to a screen reader.
 */
function TabButton({
  active,
  icon,
  label,
  needsAttention,
  onSelect,
}: {
  active: boolean
  icon: React.ReactNode
  label: string
  /** Why this tab wants the reader, or undefined when it has nothing to say. */
  needsAttention?: string
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      // The dot says this in colour and says nothing at all to a screen reader,
      // so the button carries the state in its own name. Named here rather than
      // in a hidden span beside the label: accessible names concatenate
      // adjacent text with no separator, which would come out as
      // "BacklogBacklog account not connected". The visible label stays a
      // prefix, so voice control still matches on it (WCAG 2.5.3).
      aria-label={needsAttention ? `${label} — ${needsAttention}` : undefined}
      onClick={onSelect}
      className={[
        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors',
        active
          ? 'bg-[var(--accent-light)] text-[var(--accent)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]',
      ].join(' ')}
    >
      {icon}
      <span className="min-w-0 truncate">{label}</span>
      {/* Drawn the same way as the sidebar's, because it means the same thing
          and the two are read together. */}
      {needsAttention && (
        <span
          aria-hidden="true"
          className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[var(--accent)]"
          data-testid="settings-tab-dot"
        />
      )}
    </button>
  )
}
